import { DeductionListJson } from "@/api/deduction.tsx"
import { FieldType, OrgFieldConfig } from "@/api/org_field_config.tsx"
import { DeductionTableColumn } from "./columns.tsx"
import { createOrgColumns, OrgColumnConfig } from "@/shared/org_columns.tsx"
import { month_list_field_value } from "@/shared/clearing_list.ts"

export function createOrgSpecificColumns(orgId: string, fieldConfigs: OrgFieldConfig[]): DeductionTableColumn[] {
  const config: OrgColumnConfig<DeductionListJson> = {
    tableContext: "deduction_table",
    getValue: (row, fieldName) => {
      const month_list = month_list_field_value(fieldName, row)
      if (month_list !== null) {
        return month_list
      }
      // Let it naturally show values for fields that exist on deductions
      // If the field exists on deduction, it shows; if not, it's empty
      return (row as any)[fieldName] ?? ""
    },
    getOrgId: (row) => row.org_id,
  }
  
  return createOrgColumns(orgId, fieldConfigs, config) as DeductionTableColumn[]
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("createOrgSpecificColumns", () => {
    it("computes clearing_list from the posting month and month_end_close_list from the validation date", () => {
      const columns = createOrgSpecificColumns("org", [
        {
          id: "1",
          org_id: "org",
          field_name: "clearing_list",
          field_type: FieldType.Text,
          display_name: "Clearing List",
          display_order: 0,
          is_active: true,
          table_contexts: ["deduction_table"],
          created_at: "",
          updated_at: "",
        },
      ] as OrgFieldConfig[])

      const accessor = (columns[0] as any).accessorFn
      expect(accessor({ org_id: "org", status_value: "validated", validated_date: "2026-07-20", posting_date: "2026-06-15" })).toBe(
        "June 2026"
      )
      // Still open, but it arrived in June: on June's clearing list.
      expect(accessor({ org_id: "org", status_value: "new", posting_date: "2026-06-15" })).toBe("June 2026")

      const close_columns = createOrgSpecificColumns("org", [
        { field_name: "month_end_close_list", display_name: "Month-End Close List", is_active: true, table_contexts: ["deduction_table"] },
      ] as OrgFieldConfig[])
      const close_accessor = (close_columns[0] as any).accessorFn
      expect(
        close_accessor({ org_id: "org", status_value: "validated", validated_date: "2026-07-20", posting_date: "2026-06-15" })
      ).toBe("July 2026")
      expect(close_accessor({ org_id: "org", status_value: "new", posting_date: "2026-06-15" })).toBe("")
    })

    it("reads remote_accounting_id straight off the deduction row", () => {
      // The deduction table has no static Accounting ID column, so the org column is what shows it.
      const columns = createOrgSpecificColumns("org", [
        {
          id: "1",
          org_id: "org",
          field_name: "remote_accounting_id",
          field_type: FieldType.Text,
          display_name: "ARCR",
          display_order: 0,
          is_active: true,
          table_contexts: ["deduction_table"],
          created_at: "",
          updated_at: "",
        },
      ] as OrgFieldConfig[])

      expect(columns).toHaveLength(1)
      expect((columns[0] as any).id).toBe("remote_accounting_id")
      expect(
        (columns[0] as any).accessorFn({ org_id: "org", remote_accounting_id: "Sales Deductions-ARCR-010493" })
      ).toBe("Sales Deductions-ARCR-010493")
      expect((columns[0] as any).accessorFn({ org_id: "org" })).toBe("")
    })
  })
}
